/**
 * Explore hub event taxonomy. Import the constants from here rather than
 * using string literals at call sites, so the taxonomy stays canonical.
 *
 * Props follow strict shapes defined alongside each event. Never add
 * email or email-derived props to any of these events — unique counts
 * come from the relevant DB table (e.g. feature_waitlist).
 */

import type { EditSeason } from '@/lib/season-edit/picks';

// ── Prompt 1b: submission flow ───────────────────────────────────────────
export const PHOTO_SUBMISSION_STARTED = 'photo_submission_started' as const;
export const PHOTO_SUBMISSION_COMPLETED = 'photo_submission_completed' as const;
export const PHOTO_SUBMISSION_FAILED = 'photo_submission_failed' as const;

export type PhotoSubmissionSurface =
  | 'image_slot'
  | 'content_gap'
  | 'hub_footer'
  | 'direct';

export type PhotoSubmissionFailedReason =
  | 'validation'
  | 'account'
  | 'rate_limit'
  | 'upload_error'
  | 'honeypot'
  | 'file_type'
  | 'file_size';

export type PhotoSubmissionStartedProps = {
  entry_slug: string | null;
  surface: PhotoSubmissionSurface;
};
export type PhotoSubmissionCompletedProps = {
  entry_slug: string | null;
  surface: PhotoSubmissionSurface;
  caption_included: boolean;
};
export type PhotoSubmissionFailedProps = {
  entry_slug: string | null;
  reason: PhotoSubmissionFailedReason;
};

// ── Prompt 2: hub + Winter ───────────────────────────────────────────────
export const EXPLORE_HUB_SEASON_CARD_CLICKED = 'explore_hub_season_card_clicked' as const;
export const EXPLORE_HUB_TOOLS_CLICKED = 'explore_hub_tools_clicked' as const;
export const EXPLORE_HUB_FEATURED_CLICKED = 'explore_hub_featured_clicked' as const;
export const EXPLORE_SEASON_STRIP_CLICKED = 'explore_season_strip_clicked' as const;
export const SUBMIT_YOUR_OWN_CLICKED = 'submit_your_own_clicked' as const;

export type ExploreHubSeasonCardClickedProps = {
  season: EditSeason;
  position: 1 | 2 | 3;
};
export type ExploreHubToolsClickedProps = {
  tool: 'quiz' | 'luck-factor' | 'festival-weather' | 'festival-calendar' | 'season-calendar' | 'festival-quiz';
};
export type ExploreHubFeaturedClickedProps = {
  entry_slug: string;
};
export type ExploreSeasonStripClickedProps = {
  season: EditSeason;
  strip: 'dream' | 'decide' | 'plan' | 'certify' | 'crew';
  item: string;
};
export type SubmitYourOwnClickedProps = {
  entry_slug: string | null;
  surface: string;
  page: string;
};

// ── Prompt 3: tool bridges + feature waitlist ────────────────────────────
export const EXPLORE_TOOL_BRIDGE_CLICKED = 'explore_tool_bridge_clicked' as const;
export const FEATURE_WAITLIST_SIGNUP = 'feature_waitlist_signup' as const;

export type ExploreToolBridgeClickedProps = {
  tool: 'quiz' | 'luck-factor' | 'festival-weather';
  destination: 'primary' | 'related';
};
export type FeatureWaitlistSignupProps = {
  feature_slug: 'get-hired-meter';
};

// ── Prompt 4: nav + cross-site sweep ─────────────────────────────────────
export const RELATED_IN_EXPLORE_CLICKED = 'related_in_explore_clicked' as const;
export const EXPLORE_NAV_OPENED = 'explore_nav_opened' as const;
export const EXPLORE_BREADCRUMB_CLICKED = 'explore_breadcrumb_clicked' as const;

export type RelatedInExploreClickedProps = {
  from_page: string;
  to_page: string;
  direction: 'marketplace' | 'sideways' | 'forward';
};
export type ExploreNavOpenedProps = {
  mode: 'desktop' | 'touch';
};
export type ExploreBreadcrumbClickedProps = {
  level: 'explore' | 'section' | 'self';
  from_page: string;
  to_page: string;
};

// ── Festival quiz ────────────────────────────────────────────────────────
export const FESTIVAL_QUIZ_STARTED = "festival_quiz_started" as const;
export const FESTIVAL_QUIZ_COMPLETED = "festival_quiz_completed" as const;
export const FESTIVAL_QUIZ_RESULT_CLICKED = "festival_quiz_result_clicked" as const;
export const FESTIVAL_QUIZ_SHARED = "festival_quiz_shared" as const;

// ── Destination quiz ─────────────────────────────────────────────────────
export const DESTINATION_QUIZ_STARTED = "destination_quiz_started" as const;
export const DESTINATION_QUIZ_COMPLETED = "destination_quiz_completed" as const;
export const DESTINATION_QUIZ_RESULT_CLICKED = "destination_quiz_result_clicked" as const;
export const DESTINATION_QUIZ_SHARED = "destination_quiz_shared" as const;
